import { useCallback, useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { colors } from '@modernanimal/common';

import { PromoType } from '../__generated__/graphql';
import InputField from '../components/InputField';
import PrimaryButton from '../components/PrimaryButton';
import Fonts from '../fonts';
import Icons from '../icons';
import { GET_VALID_PROMO_CODE_AND_REFERRING_HOUSEHOLD } from '../patientOnboarding/patientOnboarding.v3.gql';
import useWebBookingStore from '../store';

export type Promo = Pick<PromoType, 'id' | 'code' | 'isValid' | 'amountOff' | 'percentOff'>;

type Props = {
    promoCodeFromUrl: string;
};

const PromoCodeInput = ({ promoCodeFromUrl }: Props) => {
    const {
        client,
        patient,
        updatePatient,
    } = useWebBookingStore((state) => ({
        client: state.client,
        patient: state.patient,
        updatePatient: state.setPatient,
    }));

    // initial promo code from url is transformed to uppercase
    // for validation if present
    const [promoCode, setPromoCode] = useState(promoCodeFromUrl.toUpperCase());
    const [promo, setPromo] = useState<Promo | null>(null);
    const [isValidCode, setIsValidCode] = useState(true);
    const [isPromoAdded, setIsPromoAdded] = useState(false);
    const [isPromoManuallyCleared, setIsPromoManuallyCleared] = useState(false);

    const [getValidPromoCodeAndReferringHousehold] = useLazyQuery(GET_VALID_PROMO_CODE_AND_REFERRING_HOUSEHOLD, {
        onCompleted: (data) => {
            if (!data.validPromoCodeAndReferringHousehold) return;
            const { promo, referringHousehold } = data.validPromoCodeAndReferringHousehold;
            // NOTE: we dont support percentOff Promos in Webbooking flow
            const isValidCode = !!promo && promo.isValid && !!promo.amountOff;
            setIsValidCode(isValidCode);
            if (isValidCode) {
                setPromo(promo);
                setIsPromoAdded(true);
                updatePatient({
                    ...patient,
                    promo,
                    referringHousehold,
                });
            }
        },
    });

    const removePromo = () => {
        updatePatient({
            ...patient,
            promo: null,
        });
        setIsValidCode(true);
        setIsPromoAdded(false);
        setIsPromoManuallyCleared(true);
    };

    const validatePromoCode = useCallback(async () => {
        if (!client.household?.id) return;
        await getValidPromoCodeAndReferringHousehold({
            variables: {
                promoCode,
                referredHouseholdId: client.household.id,
            },
        });
    }, [promoCode, client, getValidPromoCodeAndReferringHousehold]);

    useEffect(() => {
        // if promo code is in the url, validate it
        if (!!promoCodeFromUrl && !isPromoAdded && !isPromoManuallyCleared) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            validatePromoCode();
        }
    }, [promoCodeFromUrl, validatePromoCode, isPromoAdded, isPromoManuallyCleared]);

    const renderPromoInput = () => (
        <PromoCodeInputContainer data-testid="promo-code-input-container">
            <Row>
                <InputContainer>
                    <InputField
                        value={promoCode}
                        placeholder="Promo code"
                        onChange={(promoCode) => setPromoCode(promoCode.toUpperCase())}
                        isInvalid={!isValidCode}
                        autoFocus
                    />
                </InputContainer>
                <ApplyButtonContainer data-testid="apply-promo-code">
                    <PrimaryButton
                        color="periwinkle"
                        onClick={validatePromoCode}
                        disabled={!promoCode}
                    >
                        Apply
                    </PrimaryButton>
                </ApplyButtonContainer>
            </Row>
            {!isValidCode && (
                <Fonts.BodySmall style={{ color: colors.RED }}>
                    Promo code cannot be validated.
                    <br />
                    <br />
                    If this is for an All Access Membership, please
                    {' '}
                    <a href="https://modernanimal.app.link/Tki5xStXiDb">download the Modern Animal app</a>
                    {' '}
                    to redeem.
                </Fonts.BodySmall>
            )}
        </PromoCodeInputContainer>
    );

    const renderAddedPromo = () => {
        if (!promo) return null;
        const { amountOff, percentOff } = promo;
        const promoDiscountAmount = amountOff ? `$${amountOff}` : `${percentOff}%`;
        return (
            <AddedPromoContainer>
                <Icons.CircleCheck height={24} width={24} fill={colors.GREEN} />
                <AddedPromoBody data-testid="added-promo-body">
                    <AddedPromoHeader>
                        <Fonts.Body>
                            {promoCode.toUpperCase()}
                            {' applied'}
                        </Fonts.Body>
                        <RemoveButton onClick={removePromo} data-testid="remove-promo-code">
                            <Fonts.LabelMedium>Remove</Fonts.LabelMedium>
                        </RemoveButton>
                    </AddedPromoHeader>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        {`A discount of ${promoDiscountAmount} has been added to your account!`}
                    </Fonts.Body>
                </AddedPromoBody>
            </AddedPromoContainer>
        );
    };

    return isPromoAdded ? renderAddedPromo() : renderPromoInput();
};

export default PromoCodeInput;

const PromoCodeInputContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;
`;

const AddedPromoBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
`;

const AddedPromoContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    padding: 12px 14px 12px 16px;
    border: 1px solid ${colors.GREEN};
    background-color: ${colors.CONTAINER_GREEN};
    border-radius: 8px;
    width: 100%;
    box-sizing: border-box;
`;

const AddedPromoHeader = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    align-items: center;
`;

const ApplyButtonContainer = styled.div`
    display: flex;
    width: 102px;
`;

const InputContainer = styled.div`
    display: flex;
    flex: 1;
`;

const RemoveButton = styled.button``;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`;
